import {
  Package,
  Truck,
  Plane,
  Factory,
  Stethoscope,
  Cpu,
  Home,
  TrendingUp,
  GraduationCap,
  Hammer,
  Check,
  X,
  ArrowRight,
} from "lucide-react";
import {
  nhEmployerSignals,
  nhTradeTracks,
  nhSalaryLadder,
} from "@/lib/northHoustonData";
import { tradeVsCollege } from "@/lib/proposalData";

const iconMap = {
  package: Package,
  truck: Truck,
  plane: Plane,
  factory: Factory,
  stethoscope: Stethoscope,
  cpu: Cpu,
};

export default function TradePipelineNH() {
  return (
    <section
      id="trades"
      data-testid="section-trade-pipeline-nh"
      className="bg-white py-24 lg:py-32 border-y border-[#CBD5E1]"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        {/* Heading */}
        <div className="grid lg:grid-cols-12 gap-10 mb-14">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-3">
              <span className="h-px w-8 bg-[#8B0000]" />
              <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#8B0000]">
                The Corridor Pipeline · Logistics · Aviation · Allied Health
              </span>
            </div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-[#0A192F] leading-tight">
              The corridor is hiring.<br />
              <em className="italic">Bridge connects the workforce.</em>
            </h2>
          </div>
          <div className="lg:col-span-5 lg:pt-3 text-[#334155] leading-relaxed">
            <p>
              The North Houston District is the largest single concentration of
              warehouse + distribution employment in greater Houston. Bridge
              routes district households into the full corridor pipeline —
              Amazon, FedEx, Sysco, Coca-Cola, Panelmatic, IAH ground services,
              Memorial Hermann Northeast — turning at-risk young adults into
              credentialed operators and homeowners within five years.
            </p>
            <p className="mt-3 font-mono text-[10px] uppercase tracking-wider text-[#64748B]">
              Sources — North Houston District corporate (2025), Houston Airports
              IAH update (2025), Workforce Solutions Greater Houston December
              2025 Labor Market Report, Lone Star College North Harris program
              data.
            </p>
          </div>
        </div>

        {/* Employer signals strip */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-[#CBD5E1] border border-[#CBD5E1] mb-16">
          {nhEmployerSignals.map((e, i) => (
            <div
              key={e.employer}
              data-testid={`trade-nh-employer-${i}`}
              className="bg-[#F9F7F1] p-6 lg:p-7 flex flex-col"
            >
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#8B0000] mb-3">
                {e.employer}
              </div>
              <div className="font-display text-5xl lg:text-6xl text-[#0A192F] leading-none">
                {e.headline}
              </div>
              <p className="mt-4 text-sm text-[#334155] leading-relaxed flex-1">
                {e.body}
              </p>
              <div className="mt-4 pt-3 border-t border-[#CBD5E1] font-mono text-[9px] uppercase tracking-widest text-[#64748B]">
                {e.source}
              </div>
            </div>
          ))}
        </div>

        {/* Workforce callout */}
        <div className="bg-[#0A192F] text-[#F9F7F1] p-8 lg:p-12 grid lg:grid-cols-12 gap-8 items-center mb-16 relative overflow-hidden">
          <div className="absolute inset-0 grain-overlay opacity-30 pointer-events-none" />
          <div className="relative z-10 lg:col-span-2">
            <Hammer className="h-14 w-14 text-[#D4AF37]" />
          </div>
          <div className="relative z-10 lg:col-span-10">
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#D4AF37] mb-2">
              Why this matters to the District
            </div>
            <h3 className="font-display text-2xl lg:text-4xl leading-tight">
              The corridor is filling its own warehouses with workers commuting in from outside the district.
            </h3>
            <p className="mt-4 text-[#CBD5E1] leading-relaxed max-w-3xl">
              The Panelmatic build alone projects ~1,000 new hires. Amazon and
              FedEx pull thousands from the surrounding metro. The most
              consequential anti-displacement lever is also the most obvious:
              get district residents into those jobs first — with childcare
              support, transportation coordination, and a credentialed pathway
              that pays from day one. That is Bridge.
            </p>
          </div>
        </div>

        {/* Trade tracks grid */}
        <div className="mb-16">
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-display text-2xl lg:text-3xl text-[#0A192F]">
              Six corridor tracks. <em className="italic">All locally hired.</em>
            </h3>
            <span className="hidden md:inline font-mono text-[10px] uppercase tracking-widest text-[#8B0000]">
              Six tracks · One pipeline
            </span>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#CBD5E1] border border-[#CBD5E1]">
            {nhTradeTracks.map((t, i) => {
              const Icon = iconMap[t.icon] || Package;
              return (
                <article
                  key={t.name}
                  data-testid={`trade-nh-track-${i}`}
                  className="bg-white p-6 lg:p-7 flex flex-col group transition-colors hover:bg-[#F9F7F1]"
                >
                  <div className="flex items-start justify-between mb-4">
                    <span className="grid place-items-center w-12 h-12 border border-[#0A192F] text-[#0A192F] group-hover:bg-[#8B0000] group-hover:border-[#8B0000] group-hover:text-[#F9F7F1] transition-colors">
                      <Icon className="h-5 w-5" />
                    </span>
                    <span className="font-mono text-[10px] uppercase tracking-widest text-[#8B0000] mt-2">
                      0{i + 1}
                    </span>
                  </div>
                  <h4 className="font-display text-xl text-[#0A192F]">{t.name}</h4>

                  <dl className="mt-4 space-y-3 text-sm">
                    <div>
                      <dt className="font-mono text-[9px] uppercase tracking-widest text-[#64748B]">
                        Starting pay
                      </dt>
                      <dd className="font-display text-lg text-[#0A192F] mt-0.5">{t.startingPay}</dd>
                    </div>
                    <div>
                      <dt className="font-mono text-[9px] uppercase tracking-widest text-[#64748B]">
                        Mid-career
                      </dt>
                      <dd className="text-[#334155]">{t.midPay}</dd>
                    </div>
                    <div>
                      <dt className="font-mono text-[9px] uppercase tracking-widest text-[#64748B]">
                        Time to credential
                      </dt>
                      <dd className="text-[#334155]">{t.timeToCredential}</dd>
                    </div>
                  </dl>

                  <div className="mt-4 pt-4 border-t border-[#CBD5E1]">
                    <div className="font-mono text-[9px] uppercase tracking-widest text-[#8B0000] mb-1">
                      Program
                    </div>
                    <div className="text-sm text-[#0A192F] font-medium">{t.program}</div>
                    <p className="mt-3 text-xs text-[#475569] leading-relaxed">{t.why}</p>
                  </div>
                </article>
              );
            })}
          </div>
        </div>

        {/* Salary ladder */}
        <div className="mb-16">
          <div className="flex items-center gap-3 mb-3">
            <span className="h-px w-8 bg-[#8B0000]" />
            <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#8B0000]">
              The 5-Year Ladder
            </span>
          </div>
          <h3 className="font-display text-2xl lg:text-4xl text-[#0A192F] leading-tight mb-2">
            From under $22,000 to <em className="italic text-[#8B0000]">homeowner in the corridor</em>.
          </h3>
          <p className="text-[#334155] max-w-3xl mb-10">
            The salary progression Bridge tracks for every district resident on
            the family-track pipeline. Each rung is a verified North Houston
            corridor wage benchmark — not a promise, but a documented path with
            employers hiring at every step.
          </p>

          <ol className="grid lg:grid-cols-5 gap-px bg-[#CBD5E1] border border-[#CBD5E1]">
            {nhSalaryLadder.map((rung, i) => {
              const isLast = rung.highlight;
              return (
                <li
                  key={rung.year}
                  data-testid={`salary-nh-rung-${i}`}
                  className={`p-6 lg:p-7 flex flex-col ${
                    isLast ? "bg-[#0A192F] text-[#F9F7F1]" : "bg-white"
                  }`}
                >
                  <div
                    className={`font-mono text-[10px] uppercase tracking-widest mb-3 ${
                      isLast ? "text-[#D4AF37]" : "text-[#8B0000]"
                    }`}
                  >
                    {rung.year}
                  </div>
                  <div
                    className={`font-display text-2xl lg:text-3xl leading-tight ${
                      isLast ? "text-[#F9F7F1]" : "text-[#0A192F]"
                    }`}
                  >
                    {rung.income}
                  </div>
                  <div
                    className={`mt-1 font-mono text-[10px] uppercase tracking-widest ${
                      isLast ? "text-[#D4AF37]" : "text-[#64748B]"
                    }`}
                  >
                    {rung.label}
                  </div>
                  <p
                    className={`mt-4 text-xs leading-relaxed flex-1 ${
                      isLast ? "text-[#CBD5E1]" : "text-[#475569]"
                    }`}
                  >
                    {rung.detail}
                  </p>
                  {isLast && (
                    <Home className="h-6 w-6 text-[#D4AF37] mt-5" data-testid="homeowner-nh-icon" />
                  )}
                  {!isLast && i < nhSalaryLadder.length - 1 && (
                    <ArrowRight className="h-4 w-4 text-[#8B0000] mt-5" />
                  )}
                </li>
              );
            })}
          </ol>
        </div>

        {/* Trade vs College */}
        <div className="bg-[#F9F7F1] border border-[#CBD5E1]">
          <div className="px-6 lg:px-10 py-7 border-b border-[#CBD5E1] flex items-center justify-between">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#8B0000] mb-1">
                The Real Comparison
              </div>
              <h3 className="font-display text-2xl lg:text-3xl text-[#0A192F]">
                Corridor credential <em className="italic">vs.</em> a 4-year degree.
              </h3>
            </div>
            <GraduationCap className="hidden md:block h-10 w-10 text-[#0A192F]" />
          </div>

          <div className="grid md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-[#CBD5E1]">
            <div data-testid="compare-nh-trade" className="p-6 lg:p-9">
              <div className="flex items-center gap-3 mb-5">
                <span className="grid place-items-center w-9 h-9 bg-[#8B0000] text-[#F9F7F1]">
                  <Package className="h-4 w-4" />
                </span>
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-[#8B0000]">
                    Bridge recommends
                  </div>
                  <div className="font-display text-xl text-[#0A192F]">
                    {tradeVsCollege.trade.label}
                  </div>
                </div>
              </div>
              <ul className="space-y-3">
                {tradeVsCollege.trade.rows.map((r) => (
                  <li key={r} className="flex items-start gap-3 text-[#0A192F]">
                    <Check className="h-5 w-5 text-[#8B0000] shrink-0 mt-0.5" />
                    <span className="text-sm leading-relaxed">{r}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div data-testid="compare-nh-college" className="p-6 lg:p-9 bg-[#F9F7F1]">
              <div className="flex items-center gap-3 mb-5">
                <span className="grid place-items-center w-9 h-9 bg-[#475569] text-[#F9F7F1]">
                  <GraduationCap className="h-4 w-4" />
                </span>
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-[#475569]">
                    The default
                  </div>
                  <div className="font-display text-xl text-[#334155]">
                    {tradeVsCollege.college.label}
                  </div>
                </div>
              </div>
              <ul className="space-y-3">
                {tradeVsCollege.college.rows.map((r) => (
                  <li key={r} className="flex items-start gap-3 text-[#475569]">
                    <X className="h-5 w-5 text-[#475569] shrink-0 mt-0.5" />
                    <span className="text-sm leading-relaxed">{r}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="px-6 lg:px-10 py-6 border-t border-[#CBD5E1] flex flex-col md:flex-row md:items-center gap-3 justify-between bg-white">
            <p className="text-sm text-[#334155] max-w-2xl flex items-start gap-2">
              <TrendingUp className="h-5 w-5 text-[#8B0000] shrink-0 mt-0.5" />
              <span>
                Bridge does not steer anyone away from college. We steer everyone
                toward the credential that produces stable corridor income —
                fastest — in the district they actually live in.
              </span>
            </p>
            <a
              href="#contact"
              data-testid="trade-nh-cta"
              className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-[#8B0000] hover:text-[#0A192F]"
            >
              Fund the pipeline <ArrowRight className="h-3.5 w-3.5" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
